import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Loader2, Upload, FileText, KeyRound, ShieldCheck, CreditCard, PartyPopper, MinusCircle, Download } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { speak } from "@/lib/speech";
import { StatusBadge, requestStatus } from "@/components/StatusBadge";
import { feeLabel } from "@/components/ServiceRow";

const Stepper = ({ steps, cur, events, completed }) => {
  const { lang } = useApp();
  const ev = Object.fromEntries(events.map((e) => [e.step, e]));
  return (
    <ol className="space-y-0" data-testid="workflow-stepper">
      {steps.map((s, i) => {
        const e = ev[s.step];
        const done = !!e && (e.status === "done" || e.status === "skipped");
        const active = !completed && s.step === cur;
        return (
          <li key={s.step} className="relative flex gap-3 pb-4" data-testid={`workflow-step-${s.step}`}>
            {i < steps.length - 1 && <span className={`absolute start-[13px] top-7 h-[calc(100%-20px)] w-0.5 ${done ? "bg-primary" : "bg-border"}`} aria-hidden="true" />}
            <span className={`z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold ${done ? (e.status === "skipped" ? "border-muted-foreground bg-muted text-muted-foreground" : "border-primary bg-primary text-white") : active ? "border-[#B8901F] bg-[hsl(var(--gold-soft))] text-[#7a5f12]" : "border-border bg-card text-muted-foreground"}`}>
              {done ? (e.status === "skipped" ? <MinusCircle size={14} /> : <Check size={14} />) : active ? <Loader2 size={13} className="animate-spin" /> : s.step}
            </span>
            <div className="min-w-0 pt-0.5">
              <p className={`text-sm font-semibold ${active ? "text-[#7a5f12] dark:text-[#E9CB75]" : ""}`}>{lang === "ar" ? s.name_ar : s.name_en}
                <span className="ms-1.5 text-[11px] font-medium text-muted-foreground">{done ? (e.status === "skipped" ? "· skipped" : "· done") : active ? "· current" : "· pending"}</span>
              </p>
              <p className="text-[11px] text-muted-foreground">{e?.detail || s.system}{e?.at ? ` · ${new Date(e.at).toLocaleString()}` : ""}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const MfaStep = ({ r, onAdvance, busy }) => {
  const { t } = useApp();
  const [code, setCode] = useState("");
  const [hint, setHint] = useState(sessionStorage.getItem(`q1_mfa_${r.ref}`));
  useEffect(() => {
    if (!hint) api.post(`/requests/${r.ref}/mfa-code`).then(({ data }) => { sessionStorage.setItem(`q1_mfa_${r.ref}`, data.sandbox_mfa_code); setHint(data.sandbox_mfa_code); });
  }, [hint, r.ref]);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onAdvance({ code }); }} className="space-y-3">
      <p className="flex items-center gap-2 text-sm font-bold"><KeyRound size={16} /> {t("mfaTitle")}</p>
      <p className="rounded-lg bg-[hsl(var(--gold-soft))] p-2 text-xs" data-testid="workflow-mfa-sandbox-code">{t("mfaHint")}: <b className="tracking-widest">{hint || "…"}</b></p>
      <input data-testid="workflow-mfa-code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="000000"
        className="h-12 w-full rounded-xl border border-input bg-card text-center text-lg tracking-[0.4em]" />
      <button data-testid="workflow-mfa-verify-btn" disabled={busy || code.length < 6} className="h-12 w-full rounded-xl bg-primary font-semibold text-primary-foreground disabled:opacity-50">{t("verify")}</button>
    </form>
  );
};

const DocsStep = ({ r, onAdvance, busy, reload }) => {
  const { t } = useApp();
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const upload = async (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File is larger than 5 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try { await api.post(`/requests/${r.ref}/documents`, fd); toast.success(`${f.name} uploaded`); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); fileRef.current.value = ""; }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm font-bold">{t("documents")}: <span className="font-normal text-muted-foreground">{r.service_docs?.join(", ")}</span></p>
      <label className="block text-xs font-semibold">{t("notes")}
        <textarea data-testid="workflow-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full rounded-xl border border-input bg-card p-3 text-sm" />
      </label>
      <label className="block text-xs font-semibold">{t("phone")}
        <input data-testid="workflow-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+974 5555 0000" className="mt-1 h-11 w-full rounded-xl border border-input bg-card px-3 text-sm" />
      </label>
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="workflow-file-input" />
      <button type="button" data-testid="workflow-upload-btn" onClick={() => fileRef.current.click()} disabled={uploading}
        className="flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed border-border p-4 text-sm font-semibold hover:border-primary">
        {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />} {t("uploadDoc")}
        <span className="text-[11px] font-normal text-muted-foreground">{t("uploadHint")}</span>
      </button>
      <DocList docs={r.documents} />
      <button data-testid="workflow-submit-docs-btn" disabled={busy} onClick={() => onAdvance({ form: { notes, phone } })} className="h-12 w-full rounded-xl bg-primary font-semibold text-primary-foreground disabled:opacity-50">{t("continue")}</button>
    </div>
  );
};

const DocList = ({ docs }) => {
  const open = async (d) => {
    const res = await api.get(`/documents/${d.doc_id}/download`, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data), "_blank");
  };
  if (!docs?.length) return null;
  return (
    <ul className="space-y-1.5" data-testid="workflow-documents-list">
      {docs.map((d) => (
        <li key={d.doc_id} className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-xs">
          <FileText size={14} /> <span className="flex-1 truncate">{d.original_filename}</span>
          <span className="text-[#0F5132] dark:text-emerald-400">✓ {d.virus_scan}</span>
          <button onClick={() => open(d)} aria-label="Download" className="p-1"><Download size={14} /></button>
        </li>
      ))}
    </ul>
  );
};

const PayStep = ({ r, onAdvance, busy }) => {
  const { t } = useApp();
  const [method, setMethod] = useState("QPay");
  return (
    <div className="space-y-3">
      <p className="flex items-center gap-2 text-sm font-bold"><CreditCard size={16} /> {t("payMethod")}</p>
      <div className="rounded-2xl bg-gradient-to-br from-[#8A1538] to-[#4a0b1f] p-4 text-white">
        <p className="text-xs text-white/70">QPay / QNB · Sandbox checkout</p>
        <p className="mt-1 text-2xl font-extrabold" data-testid="workflow-pay-amount">{r.fee_qar} QAR</p>
        <p className="text-xs text-white/70">{r.ref}</p>
      </div>
      <div className="grid grid-cols-3 gap-2" role="radiogroup">
        {["QPay", "QNB Debit", "Card (simulated)"].map((m) => (
          <button key={m} role="radio" aria-checked={method === m} data-testid={`workflow-pay-method-${m.split(" ")[0].toLowerCase()}`} onClick={() => setMethod(m)}
            className={`min-h-[48px] rounded-xl border-2 px-2 text-xs font-semibold ${method === m ? "border-primary bg-[hsl(var(--maroon-soft))] text-primary" : "border-border bg-card"}`}>{m}</button>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">{t("paymentSimulated")}</p>
      <button data-testid="workflow-pay-btn" disabled={busy} onClick={() => onAdvance({ method })} className="h-12 w-full rounded-xl bg-[#B8901F] font-semibold text-white disabled:opacity-50">{t("payNow")} {r.fee_qar} QAR</button>
    </div>
  );
};

export default function RequestPage() {
  const { ref } = useParams();
  const { t, svcName, catalog, voiceLang, refreshMe } = useApp();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/requests/${ref}`).then(({ data }) => setR(data)).catch((e) => setErr(errMsg(e))), [ref]);
  useEffect(() => { load(); }, [load]);

  const advance = useCallback(async (payload = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/requests/${ref}/advance`, { step: r.workflow_step, ...payload });
      await load();
      if (data.status === "Completed") {
        toast.success(t("done"));
        speak(`${t("doneSpoken")} ${ref}`, voiceLang === "ar" ? "ar" : voiceLang === "en" ? "en" : "en");
        refreshMe();
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }, [r, ref, load, t, voiceLang, refreshMe]);

  useEffect(() => {
    if (!r || r.status === "Completed" || r.workflow_step < 5 || busy) return;
    const id = setTimeout(() => advance(), 1400);
    return () => clearTimeout(id);
  }, [r, busy, advance]);

  if (err) return <p className="p-6 text-center text-sm" data-testid="request-error">{err}</p>;
  if (!r || !catalog) return <div className="m-4 h-72 animate-pulse rounded-2xl bg-muted" />;
  const svc = catalog.services.find((s) => s.service_id === r.service_id);
  const full = { ...r, service_docs: svc?.documents_required };
  const completed = r.status === "Completed";
  const cur = r.workflow_step;
  const autoMsg = { 5: t("processing"), 6: t("notifying"), 7: t("done") }[cur];

  return (
    <div className="space-y-4 px-4 pt-4" data-testid="request-page">
      <div className="rounded-2xl border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs font-semibold text-muted-foreground" data-testid="request-ref">{r.ref}</span>
          <StatusBadge status={requestStatus(r)} testId="request-status-badge" />
        </div>
        <h1 className="mt-1 text-lg font-bold leading-snug">{svcName(r.service)}</h1>
        <p className="text-xs text-muted-foreground">{t("step")} {cur} {t("of")} 7 · {feeLabel(r.fee_qar, t)} · {r.channel}</p>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${((completed ? 7 : cur - 1) / 7) * 100}%` }} />
        </div>
      </div>
      {!completed && (
        <div className="q1-fade-up rounded-2xl border-2 border-[hsl(var(--accent)/0.5)] bg-card p-4 shadow-sm" data-testid="workflow-current-panel">
          {cur === 1 && <MfaStep r={r} onAdvance={advance} busy={busy} />}
          {cur === 2 && (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm font-bold"><ShieldCheck size={16} /> {t("eligibility")}</p>
              <p className="text-xs text-muted-foreground">{svc?.eligible_personas.join(" · ")}</p>
              <p className={`text-xs font-semibold ${r.eligible ? "text-[#0F5132] dark:text-emerald-400" : "text-[#842029] dark:text-red-400"}`} data-testid="workflow-eligibility-result">{r.eligible ? `✓ ${t("eligible")}` : `✕ ${t("notEligible")} — ${t("switchTo")}: ${svc?.eligible_personas.join(", ")}`}</p>
              <button data-testid="workflow-eligibility-btn" disabled={busy || !r.eligible} onClick={() => advance()} className="h-12 w-full rounded-xl bg-primary font-semibold text-primary-foreground disabled:opacity-50">{t("continue")}</button>
            </div>
          )}
          {cur === 3 && <DocsStep r={full} onAdvance={advance} busy={busy} reload={load} />}
          {cur === 4 && <PayStep r={r} onAdvance={advance} busy={busy} />}
          {cur >= 5 && <p className="flex items-center justify-center gap-2 py-3 text-sm font-semibold" data-testid="workflow-auto-status"><Loader2 size={16} className="animate-spin" /> {autoMsg}</p>}
        </div>
      )}
      {completed && (
        <div className="q1-fade-up flex items-center gap-3 rounded-2xl border border-[#BADBCE] bg-[#D1E7DD] p-4 text-[#0F5132]" data-testid="workflow-completed-banner">
          <PartyPopper size={24} /> <div><p className="font-bold">{t("done")}</p><p className="text-xs">{r.completed_at ? new Date(r.completed_at).toLocaleString() : ""}</p></div>
        </div>
      )}
      {r.payment && (
        <div className="rounded-2xl border bg-card p-4 text-sm shadow-sm" data-testid="request-payment-receipt">
          <p className="font-bold">{t("receipt")}</p>
          <p className="text-xs text-muted-foreground">{r.payment.method} · {r.payment.masked_ref} · {r.payment.txn_id}</p>
          <p className="mt-1 font-semibold">{r.payment.amount_qar} QAR · {r.payment.status}</p>
        </div>
      )}
      {completed && <DocList docs={r.documents} />}
      <div className="rounded-2xl border bg-card p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-bold">{t("timeline")}</h2>
        <Stepper steps={catalog.workflow_steps} cur={cur} events={r.events} completed={completed} />
      </div>
      <Link to="/requests" className="block text-center text-sm font-semibold text-primary" data-testid="request-back-to-list">{t("myRequests")}</Link>
    </div>
  );
}
